import { useLazyQuery, useQuery } from '@vue/apollo-composable';

import type { ProgrammeType, ProcessedResult } from '@/types/composition-functions';
import { useListSchoolProgrammesWithTemplatesLazyQuery, useGetStudentResultTertiaryLazyQuery } from '@/types/vue-apollo-helpers';
import { GET_PROGRAMME_WITH_TEMPLATES, GET_STUDENT_TRANSFERABLE_COURSES } from '@/graphql';

/**
 * Composable to get a single programme with its result templates
 */
export function useProgrammeWithTemplates(programmeId?: string) {
  const variables = ref({ id: programmeId || '' });

  const { result, loading, error, refetch, onResult } = useQuery(GET_PROGRAMME_WITH_TEMPLATES, variables,
    {
      enabled: !!programmeId,
    },
  );

  const programme = computed<ProgrammeType | null>(
    () => result.value?.getProgramme || null,
  );

  const resultTemplates = computed(
    () => programme.value?.resultTemplates || [],
  );

  const fetchProgramme = async(id: string) => {
    variables.value.id = id;
    return refetch({ id });
  };

  return {
    programme,
    resultTemplates,
    loading,
    error,
    refetch,
    fetchProgramme,
    onResult,
  };
}

/**
 * Composable to list programmes with their result templates
 */
export function useProgrammesWithTemplates(options: {
  autoFetch?: boolean;
  first?: number;
  page?: number;
} = {}) {
  const { autoFetch = false, first = 10, page = 1 } = options;
  const variables = ref({ first, page, filter: {} });

  const { result, loading, error, refetch, load, onResult } = useListSchoolProgrammesWithTemplatesLazyQuery(variables.value,
    {
      fetchPolicy: 'cache-and-network',
    },
  );

  // Auto-fetch if enabled
  if (autoFetch) {
    load();
  }

  const programmes = computed<ProgrammeType[]>(
    () => result.value?.listSchoolProgrammes?.data || [],
  );

  const paginatorInfo = computed(
    () => result.value?.listSchoolProgrammes?.paginatorInfo,
  );

  const setPage = async(newPage: number) => {
    variables.value.page = newPage;
    await refetch({ ...variables.value });
  };

  const setFilter = async(filter: Record<string, unknown>) => {
    variables.value.filter = filter;
    variables.value.page = 1;
    await refetch({ ...variables.value });
  };

  return {
    programmes,
    paginatorInfo,
    loading,
    error,
    refetch,
    load,
    onResult,
    setPage,
    setFilter,
  };
}

/**
 * Composable to get student result for tertiary (includes GPA/CGPA)
 */
export function useStudentResultTertiary(options: {
  studentId?: string;
  academicCalendarId?: string;
  autoFetch?: boolean;
} = {}) {
  const { studentId, academicCalendarId, autoFetch = false } = options;

  const variables = ref({
    student_id: studentId || '',
    academic_calendar_id: academicCalendarId || '',
  });

  const { result, loading, error, refetch, load, onResult } = useGetStudentResultTertiaryLazyQuery(variables.value,
    {
      fetchPolicy: 'cache-and-network',
    },
  );

  if (autoFetch && studentId && academicCalendarId) {
    load();
  }

  const studentResult = computed<ProcessedResult | null>(
    () => result.value?.studentResult || null,
  );

  const headers = computed(() => studentResult.value?.headers || []);
  const data = computed(() => studentResult.value?.data || []);
  const summary = computed(() => studentResult.value?.summary || {});
  const status = computed(() => studentResult.value?.status);
  const student = computed(() => studentResult.value?.student);
  const programme = computed(() => studentResult.value?.programme);
  const template = computed(() => studentResult.value?.template);
  const feedbacks = computed(() => studentResult.value?.result_feedbacks || []);

  const fetchResult = async(newStudentId: string, newCalendarId: string) => {
    variables.value.student_id = newStudentId;
    variables.value.academic_calendar_id = newCalendarId;
    return refetch({
      student_id: newStudentId,
      academic_calendar_id: newCalendarId,
    });
  };

  return {
    studentResult,
    headers,
    data,
    summary,
    status,
    student,
    programme,
    template,
    feedbacks,
    loading,
    error,
    refetch,
    load,
    fetchResult,
    onResult,
  };
}

/**
 * Composable to get student's transferable courses
 */
export function useStudentTransferableCourses(options: {
  studentId?: string;
  academicCalendarId?: string;
  autoFetch?: boolean;
} = {}) {
  const { studentId, academicCalendarId, autoFetch = false } = options;

  const variables = ref({
    studentId: studentId || '',
    academicCalendarId: academicCalendarId || undefined,
  });

  const { result, loading, error, refetch, load, onResult } = useLazyQuery(GET_STUDENT_TRANSFERABLE_COURSES, variables.value,
    {
      fetchPolicy: 'cache-and-network',
    },
  );

  if (autoFetch && studentId) {
    load();
  }

  const courseRegistrations = computed(
    () => result.value?.studentCourseRegistrations || [],
  );

  // Flatten all courses from registrations
  const allCourses = computed(() => {
    const courses: Array<{
      id: string;
      course: {
        id: string;
        course_code: string;
        course_title: string;
        unit: number;
      };
      is_transferred: boolean;
      accepted_for_programme_id?: string;
      acceptedForProgramme?: { id: string; program_name: string };
      registration: {
        id: string;
        programme_id: string;
        semester: number;
        registration_level: string;
      };
    }> = [];

    for (const reg of courseRegistrations.value) {
      for (const courseReg of reg.coursesRegistered || []) {
        courses.push({
          ...courseReg,
          registration: {
            id: reg.id,
            programme_id: reg.programme_id,
            semester: reg.semester,
            registration_level: reg.registration_level,
          },
        });
      }
    }

    return courses;
  });

  // Courses that can be transferred (not already transferred)
  const transferableCourses = computed(() =>
    allCourses.value.filter((c) => !c.is_transferred),
  );

  // Courses that have been transferred
  const transferredCourses = computed(() =>
    allCourses.value.filter((c) => c.is_transferred),
  );

  const fetchCourses = async(newStudentId: string, newCalendarId?: string) => {
    variables.value.studentId = newStudentId;
    variables.value.academicCalendarId = newCalendarId;
    return refetch({
      studentId: newStudentId,
      academicCalendarId: newCalendarId,
    });
  };

  return {
    courseRegistrations,
    allCourses,
    transferableCourses,
    transferredCourses,
    loading,
    error,
    refetch,
    load,
    fetchCourses,
    onResult,
  };
}
